import {
  Button,
  Flex,
  Form,
  Icon,
  Modal,
  PaginationBar,
  Text,
  Title,
} from '@wyz/ui';
import { useDebouncedValue, useOnUpdate } from '@wyz/utils';
import React, { useState } from 'react';
import { Trans, useI18n } from '~/common/context';
import { useManageS2SBusinessRules } from '~/connect/api';
import {
  IS2SBusinessRuleFormEntry,
  IS2SFlowTemplateForm,
} from '~/connect/types';
import { BusinessRuleParameters } from '~/connect/components';
import { useFormContext } from 'react-hook-form';

const ITEMS_PER_PAGE = 5;

type Props = {
  open: boolean;
  onClose: () => void;
  selectedRules: IS2SBusinessRuleFormEntry[];
  onConfirm: (rules: IS2SBusinessRuleFormEntry[]) => void;
};

export function BusinessRulesModal({
  open,
  onClose,
  selectedRules,
  onConfirm,
}: Props) {
  const { t } = useI18n();
  const { formState, clearErrors, trigger } =
    useFormContext<IS2SFlowTemplateForm>();

  const [page, setPage] = useState(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [textQuery, debounceHandlers] = useDebouncedValue('', {
    debounceTimeout: 300,
  });
  const [pendingRules, setPendingRules] = useState<IS2SBusinessRuleFormEntry[]>(
    [...selectedRules],
  );

  React.useEffect(() => {
    if (open) {
      setPendingRules([...selectedRules]);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useOnUpdate(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textQuery]);

  const businessRules = useManageS2SBusinessRules({
    getAll: {
      enabled: open,
    },
  });

  const allBusinessRules = React.useMemo(
    () => businessRules.getAll.data?.data ?? [],
    [businessRules.getAll.data?.data],
  );
  const normalizedTextQuery = textQuery.trim().toLowerCase();
  const filteredBusinessRules = React.useMemo(() => {
    if (!normalizedTextQuery) {
      return allBusinessRules;
    }

    return allBusinessRules.filter((rule) => {
      const translatedName = t(rule.name.i18nKey);
      const translatedDescription = t(rule.description.i18nKey);
      const searchableName = (
        translatedName || rule.name.defaultValue
      ).toLowerCase();
      const searchableDescription = (
        translatedDescription || rule.description.defaultValue
      ).toLowerCase();

      return (
        searchableName.includes(normalizedTextQuery) ||
        searchableDescription.includes(normalizedTextQuery)
      );
    });
  }, [allBusinessRules, normalizedTextQuery, t]);

  const paginatedBusinessRules = React.useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredBusinessRules.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredBusinessRules, page]);

  const handleAdd = (id: string, paramKeys: string[]) => {
    if (!pendingRules.some((r) => r.id === id)) {
      const businessRule = allBusinessRules.find((r) => r.id === id);
      if (!businessRule) return;
      const defaultValueLookup: Record<string, string> = {};
      businessRule.parameterDefinitions.forEach((def) => {
        defaultValueLookup[def.key] = def.defaultValue ?? '';
      });
      setPendingRules((prev) => [
        ...prev,
        {
          id,
          parameters: Object.fromEntries(
            paramKeys.map((k) => [k, defaultValueLookup[k] ?? '']),
          ),
        },
      ]);
    }
  };

  const handleRemove = (id: string) => {
    setPendingRules((prev) => prev.filter((r) => r.id !== id));
  };

  const handleParamChange = (id: string, key: string, value: string) => {
    setPendingRules((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, parameters: { ...r.parameters, [key]: value } }
          : r,
      ),
    );
  };

  const handleConfirm = async () => {
    onConfirm(pendingRules);
    const isValid = await trigger('businessRules');
    if (isValid) {
      onClose();
    }
  };

  return (
    <Modal.Container open={open} onClose={onClose} size='lg'>
      <Modal.Header>
        <Modal.Title>
          <Title heading='page-title' weight='bold'>
            <Trans i18nKey='app.s2s_flow_template.form.business_rules.modal.title' />
          </Title>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-3'>
          <Form.Control
            type='search'
            placeholder={t(
              'app.s2s_flow_template.form.business_rules.modal.search',
            )}
            onChange={({ target }) => debounceHandlers.onChange(target.value)}
          />
        </div>

        <div className='d-flex flex-column gap-2'>
          {paginatedBusinessRules.map((rule) => {
            const pendingEntryIndex = pendingRules.findIndex(
              (r) => r.id === rule.id,
            );
            const pendingEntry =
              pendingEntryIndex !== -1
                ? pendingRules[pendingEntryIndex]
                : undefined;
            const isAdded = Boolean(pendingEntry);
            const ruleErrors =
              formState.errors?.businessRules?.[pendingEntryIndex]?.parameters;
            return (
              <Flex
                key={rule.id}
                justifyContent='between'
                alignItems='start'
                className={`p-3 border rounded ${
                  isAdded ? 'bg-light' : 'bg-white'
                }`}
              >
                <div className='flex-grow-1'>
                  <Flex direction='column' className='mb-2'>
                    <Text component='span' heading='normal' weight='semi-bold'>
                      <Trans i18nKey={rule.name.i18nKey} />
                    </Text>
                    <Text component='span' heading='sm' weight='light'>
                      <Trans i18nKey={rule.description.i18nKey} />
                    </Text>
                  </Flex>
                  {isAdded && rule.parameterDefinitions.length > 0 && (
                    <BusinessRuleParameters
                      mode='editable'
                      definitions={rule.parameterDefinitions}
                      values={pendingEntry?.parameters ?? {}}
                      onChange={(key, value) => {
                        handleParamChange(rule.id, key, value);
                        clearErrors(
                          `businessRules.${pendingEntryIndex}.parameters.${key}`,
                        );
                      }}
                      ruleErrors={ruleErrors}
                    />
                  )}
                </div>
                {isAdded ? (
                  <Button
                    id={`remove-rule-${rule.id}`}
                    variant='secondary'
                    size='sm'
                    type='button'
                    onClick={() => handleRemove(rule.id)}
                  >
                    <Icon icon='minus' width='1rem' />
                    <Trans i18nKey='app.common.buttons.remove' />
                  </Button>
                ) : (
                  <Button
                    id={`add-rule-${rule.id}`}
                    variant='link'
                    size='sm'
                    type='button'
                    onClick={() =>
                      handleAdd(
                        rule.id,
                        rule.parameterDefinitions.map((d) => d.key),
                      )
                    }
                  >
                    <Icon icon='plus' width='1rem' />
                    <Trans i18nKey='app.common.button.add' />
                  </Button>
                )}
              </Flex>
            );
          })}
        </div>

        <Flex justifyContent='between' alignItems='center' className='mt-3'>
          <Text color='secondary-gray' heading='xs'>
            <Trans
              i18nKey='app.common.datatable.pagination.items_per_page_count'
              values={{ count: ITEMS_PER_PAGE }}
            />
          </Text>
          <PaginationBar
            limit={ITEMS_PER_PAGE}
            total={filteredBusinessRules.length}
            page={page}
            onPageChange={setPage}
          />
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Flex className='w-100 gap-3'>
          <Button
            id='cancel-business-rules'
            variant='secondary'
            type='button'
            className='flex-grow-1'
            onClick={onClose}
          >
            <Trans i18nKey='app.common.button.cancel' />
          </Button>
          <Button
            id='confirm-business-rules'
            variant='primary'
            type='button'
            className='flex-grow-1'
            onClick={handleConfirm}
          >
            <Trans i18nKey='app.common.buttons.validate' />
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal.Container>
  );
}
